import { DatePipe } from '@angular/common';
import {
  UserCreationRequestDTO,
  UserDisableEnableRequestDTO,
  UserDTO,
  UserResponseDTO,
  UserUpdateRequestDTO,
} from '../models/UserDTO';
import { UserUIModel } from '../models/UserUIModel';
import { environment } from '../../../../../environments/environment';

function sanitizeUserFormat(user: {
  firstName: string;
  lastName: string;
  email: string;
}) {
  // The Regex matches whitespace, hyphen and apostrophes in the string and then uppercases the next following non-whitespace character,
  // e.g "o'connor" results in "O'Connor"
  const capitalizeWords = (name: string): string =>
    name
      .toLowerCase()
      .replace(
        /(^|[ \-'])(\S)/g,
        (_: string, sep: string, char: string) => sep + char.toUpperCase(),
      );

  return {
    firstName: capitalizeWords(user.firstName),
    lastName: capitalizeWords(user.lastName),
    email: user.email.toLowerCase(),
  };
}

export function formatDate(date: string): string | null {
  const fallbackLocale = environment.general.internationalFallbackLanguage;
  const locale = navigator.language;

  try {
    const datePipe = new DatePipe(locale);
    return datePipe.transform(date, 'yyyy');
  } catch {
    const fallbackDatePipe = new DatePipe(fallbackLocale);
    return fallbackDatePipe.transform(date, 'yyyy');
  }
}

export function transformUserFormDataToUserDTO(
  userFormData: Partial<UserDTO>,
): UserDTO {
  if (
    userFormData.firstName === undefined ||
    userFormData.lastName === undefined ||
    userFormData.email === undefined
  ) {
    throw new Error();
  }
  return {
    firstName: userFormData.firstName,
    lastName: userFormData.lastName,
    email: userFormData.email,
    isAdmin: userFormData.isAdmin ?? false,
  };
}

export function toUsersCreationRequestDTO(
  users: UserDTO[] | undefined,
): UserCreationRequestDTO {
  if (users === undefined) {
    throw new Error();
  }
  const userCreationRequestDTO = new UserCreationRequestDTO();
  userCreationRequestDTO.users = users.map((user) => ({
    ...sanitizeUserFormat(user),
    isAdmin: user.isAdmin,
  }));
  return userCreationRequestDTO;
}

export function toUserDisableEnableRequestDTO(
  users: UserUIModel[],
): UserDisableEnableRequestDTO {
  const userDisableEnableRequestDTO = new UserDisableEnableRequestDTO();
  userDisableEnableRequestDTO.users = users.map((user) => ({ id: user.id }));
  return userDisableEnableRequestDTO;
}

export function toUserUpdateRequestDTO(
  user: Partial<UserUIModel>,
): UserUpdateRequestDTO {
  if (!user.firstName || !user.lastName || !user.email) {
    throw new Error();
  }

  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
  } as UserUpdateRequestDTO;
}

export function toUserUIModel(users: UserResponseDTO[]): UserUIModel[] {
  return users.map((user) => {
    const username = `${user.firstName} ${user.lastName}`;
    const {
      firstName,
      lastName,
      email,
      role,
      id,
      licenses,
      accountEnabled,
      createdAt,
      syncStatus,
    } = user;

    // for the MMP we assume that there is either one license per user (index 0 of licenses) or licenses are null
    const license = user.licenses.length
      ? { status: licenses[0].status, id: licenses[0].id }
      : null;

    return {
      id,
      username,
      firstName,
      lastName,
      role,
      email,
      syncStatus,
      accountEnabled,
      createdAt: createdAt ? formatDate(createdAt) : null,
      license,
    };
  });
}
